(ns frontend.view.entity-viewers.modules.tasks.view
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [ui.state.named-values :as named-values]
   [ui.util :as uu]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.time :as c-time]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.modules.tasks.event :as event]
   [frontend.view.entity-viewers.modules.tasks.sub :as sub]
   [frontend.view.grid.tasks-grid.events :as tasks-events]
   [frontend.view.modals.activity-entity-editor.view :as activity-entity-editor]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [re-frame.core :as rf]
   [reagent.core :as r]


   [ui.components.badge.view :as badge]
   [ui.components.radix-ui.avatar.view :as avatar]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.page-components.filter-bar.view :as filter-bar]
   [ui.components.button.view :as button]))

(defn task-status-badge
  [{:keys [task/completed?]}]
  (let []
    [:span
     {:class (uu/cm "p-1 flex items-center text-xs rounded bg-warning-light text-warning-light-text font-semibold"
                    (when completed? "bg-success-light text-success-light-text"))}
     (if completed?
       (loc/tr :done)
       (loc/tr :to-do))]))

(defn owner-avatar
  [task]
  (let [task-owner (some-> task
                           :task/assignee
                           (session/get-entity))
        image-url (:account/picture-url task-owner)
        owner-name (entity/display-string task-owner)
        initials (entity/get-initials task-owner)
        initials (if (= initials "")
                   nil
                   initials)]
    [tooltip/view
     [:span
      [avatar/view
       {:image-url image-url
        :size      :small
        :fallback  (or initials
                       [:i.fal.fa-user])}]]
     [:span (loc/tr :task-owner- [owner-name])]]))

(defn task-due
  [task]
  (let [due-ts (:task/due task)]
    (when due-ts
      [:div
       [:span
        {:class "mb-0 text-sm text-base-light-text-accent"}
        (str (string/capitalize (loc/tr :due))
             " "
             (fu/display-date-timestamp due-ts))]])))

(defn task-content
  [task]
  (let [title (:task/title task)
        message (some-> task
                        :task/message
                        (fu/html->plain-text))]
    [:div
     {:class "truncate grow"}
     (cond (not (cu/empty-value? title))
           [:span
            {:class "font-semibold"}
            title]
           (not (cu/empty-value? message))
           [:span
            {:class "block mb-0 text-sm truncate max-h-10"}
            message])]))

(defn task-actions
  [{:keys [task open-task-editor state-atom visible?]}]
  (let [completed? (:task/completed? task)
        open-delete-confirmation (fn []
                                   (swap! state-atom assoc :hover? false)
                                   (modals/open-modal!
                                    confirmation/modal-key
                                    {:body          [:div
                                                     [:p (loc/tr :confirm-delete-task)]]
                                     :confirm-text  (loc/tr :delete-entity [(string/lower-case (loc/tr :entity.type/task))])
                                     :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
                                     :on-confirm    #(entity-actions/*->disable!
                                                      (:db/id task)
                                                      identity
                                                      identity)}))]
    [:div
     {:class     (uu/cm "invisible group-hover:visible flex items-center justify-center"
                        (when visible? "visible"))
      :on-click (fn [e]
                  (.preventDefault e)
                  (.stopPropagation e))}
     [dropdown-menu/view
      {:items
       (concat
        (when-not completed?
          [{:value      :complete-task
            :label      (loc/tr :complete-task)
            :icon-class "fal fa-check-square"
            :on-click   (partial tasks-events/complete-task! task)}])
        [{:value      :edit-task
          :icon-class "fal fa-pen"
          :label      (loc/tr :edit-entity [(string/lower-case (loc/tr :entity.type/task))])
          :on-click   open-task-editor}
         {:value      :delete-task
          :icon-class "fal fa-trash"
          :label      (loc/tr :delete-entity [(string/lower-case (loc/tr :entity.type/task))])
          :on-click   open-delete-confirmation}])
       :on-open-change (fn [open?]
                         (swap! state-atom assoc :dropdown-open? open?))
       :props          {:align       :end
                        :side-offset 0}
       :trigger
       [:span
        [:i.fal.fa-lg.fa-ellipsis-v.clickable]]}]]))

(defn task-card
  [{:keys [task]}]
  (let [state-atom (r/atom {:hover? false})]
    (fn [{:keys [task]}]
      (let [{:keys [hover?]} @state-atom
            open-task-editor (fn []
                               (without-default-action-and-propagation
                                (when-not (session/is-read-only-user?)
                                  (swap! state-atom assoc :hover? false)
                                  (case (:task/type task)
                                    :task.type/webentry (tasks-events/handle-webentry task)
                                    :task.type/email (tasks-events/handle-email task)
                                    (modals/open-modal! activity-entity-editor/modal-key
                                                        {:entity-id (:db/id task)})))))]
        [:div
         {:role           "button"
          :tab-index      "0"
          :on-mouse-enter #(swap! state-atom assoc :hover? true)
          :on-mouse-leave #(swap! state-atom assoc :hover? false)
          :on-click       open-task-editor}
         [task-status-badge task]
         [owner-avatar task]

         [task-content task]
         [task-due task]
         [task-actions
          {:task             task
           :open-task-editor open-task-editor
           :state-atom       state-atom}]]))))

(defn no-tasks-view
  [viewed-entity-id]
  (let [all-tasks @(rf/subscribe [::sub/view-tasks viewed-entity-id])]
    [:div
     {:class "flex flex-col gap-3"}
     (if (seq all-tasks)
       [:p
         {:class "mb-0 text-base-light-text-paler text-sm"}
        (loc/tr :no-matching-tasks)]
       [:p
        {:class "mb-0 text-base-light-text-paler text-sm"}
        (loc/tr :no-tasks-yet)])]))

(defn task-row
  [task]
  (let [state-atom (r/atom {})]
    (fn [task]
      (let [open-task-editor (fn [e]
                               (when e
                                 (.preventDefault e)
                                 (.stopPropagation e))

                               (when-not (session/is-read-only-user?)
                                 (swap! state-atom assoc :hover? false)
                                 (case (:task/type task)
                                   :task.type/webentry (tasks-events/handle-webentry task)
                                   :task.type/email (tasks-events/handle-email task)
                                   (do
                                     (modals/open-modal! activity-entity-editor/modal-key
                                                         {:entity-id (:db/id task)}))))
                               false)
            due (:task/due task)
            overdue? (and (not (cu/blank-value? due))
                          (< due (c-time/now-millis)))]
        [:li
         {:class          "px-2 py-2 flex items-start gap-2 flex-1
                           cursor-pointer border border-solid border-transparent border-b-base-light-border-paler
                           hover:border-base-light-border-accent group"
          :on-mouse-enter #(swap! state-atom assoc :hover? true)
          :on-mouse-leave #(swap! state-atom assoc :hover? false)
          :on-click       #(rf/dispatch [::event/open-task task])}
         [:div
          {:class "flex flex-col gap-1 flex-1"}
          [:label
           {:class "font-bold leading-4"}
           (:task/title task)]
          [:div
           {:class "[&_.ProseMirror]:p-0 [&_.ProseMirror]:border-0 [&_.ProseMirror]:bg-transparent"}
           [tiptap/viewer
            {:value (:task/message task)
             :styles {:backgroundColor "transparent"}}]]]
         [:div
          {:class "pt-10 m-0 text-sm"}
          (when-not (cu/blank-value? due)
            (loc/tr :task-due-by [(fu/format-date-ts due)]))]
         [:div
          {:class "pt-10"}
          (if (:task/completed? task)
            [badge/success
             {:label (loc/tr :task-done)}]

            (if overdue?
              [badge/danger
               {:label (loc/tr :overdue)}]
              [badge/warning
               {:label (loc/tr :task-todo)}]))]
         [:div
          {:class "pt-10"}
          [task-actions
           {:task             task
            :open-task-editor open-task-editor
            :state-atom       state-atom}]]]))))

(defn tasks-view
  [task-entities]
  [:ul
   {:class "flex flex-col gap-2"}
   (->> task-entities
        (map (fn [task]
               ^{:key (str "task-" (:db/id task))}
               [task-row task]))
        (doall))])

(defn view
  [viewed-entity-id]
  (r/create-class
   {:component-did-mount
    (fn []
      ;; Task tags are named-values, not bulk-synced on the new grid - ask for
      ;; them once so the tag filter has options. Idempotent.
      (rf/dispatch [::named-values/ensure-loaded :task/tag]))
    :reagent-render
    (fn [viewed-entity-id]
      (let [component-id (str "tasks-" viewed-entity-id)
            task-entities @(rf/subscribe [::sub/visible-task-entities component-id viewed-entity-id])
            task-filters @(rf/subscribe [::sub/task-filters])
            init-filters @(rf/subscribe [::sub/init-filters])]
        ^{:key component-id}
        [:div
         {:class "pt-2 -mx-3"}
         [filter-bar/view
          {:component-id component-id
           :all-filters  task-filters
           :init-filters init-filters}]
         (if (seq task-entities)
           [tasks-view task-entities]
           [no-tasks-view viewed-entity-id])]))}))

(defn add-new-task-button
  [viewed-entity-id]
  (let []
    [:div
    [button/secondary-open 
      {:role       "button"
       :tooltip  (loc/tr :add-task)
       :tab-index  "0"
       :on-click   (fn [_]
                    (modals/open-modal! activity-entity-editor/modal-key
                                        {:new-entity-type  :entity.type/task
                                         :viewed-entity-id viewed-entity-id}))}
     [:i
      {:class-name (fu/cs "fal clickable fa-plus-circle")}]]]))

(defn header-view
  [viewed-entity-id]
  (let []
    [:div
     [add-new-task-button viewed-entity-id]]))
